// The model gallery — a standalone browser page to browse every model the
// library holds. The landing view leads with a row of themed *collections*
// (Buildings, Animals, Military, Water…), each a tile previewing a few of its
// members; click one to open its own page — the section title and description up
// top, with only that collection's items below (a back arrow returns you). The
// row's first tile opens the *batch index* (`#batches`): a tile per production
// run, each opening that batch's page (`#batch=<id>`) — the same titled-header +
// filtered-grid view as a collection, but keyed on a model's `batchId`
// (provenance) rather than a tag.
// Search by name/id/tag narrows further. Several files can describe the same object in
// different styles ("avenue", "avenue-v2", …) — the grid merges those into one
// tile per object, badged with its version count and showing a strip of mini
// previews (hover one to swap the big preview); the detail viewer then exposes
// each version (rotate, zoom, step variants, play animations, copy the source).
// Navigation is reversible: going back (the browser button, a "‹ Back"/Esc, or
// the detail's "‹ Gallery") returns you to the exact view you left — the same
// collection/search in force and the page scrolled to the same row — because
// each history entry carries its own scroll offset (see "State restoration"
// below). Every model is
// auto-discovered from src/models at build time, so this page never needs
// editing when one is added.

import { search, type Model } from "../lib";
import { discoverModels } from "../registry/discover.browser";
import type { LoadedModel } from "../registry/registry";
import { ThumbStudio } from "./thumbs";
import { DetailViewer, type DetailItem } from "./detail";
import { SlotTypeViewer } from "./slot-type-viewer";
import { COLLECTIONS, type Collection } from "./collections";
import { BATCHES, batchById } from "../batches";
import { SLOT_TYPES, slotTypeById } from "../slot-types";
import { groupByObject, versionLabel, type VersionGroup } from "./groups";

// ── Catalogue ──────────────────────────────────────────────────────────────
interface Item extends LoadedModel { category: string; }

function categoryOf(path: string): string {
  const seg = path.split("/")[0] ?? "misc";
  return seg.charAt(0).toUpperCase() + seg.slice(1);
}

const items: Item[] = discoverModels()
  .map((l) => ({ ...l, category: categoryOf(l.path) }))
  .sort((a, b) => a.category.localeCompare(b.category) || a.model.name.localeCompare(b.model.name));
const models: Model[] = items.map((i) => i.model);
const defaultVariant = (m: Model): string => m.defaultVariantId;

// Merge same-object versions into one tile each; index every member id back to
// its group so a deep link to any version ("#avenue-v2") lands in the right tile.
const groups: VersionGroup<Item>[] = groupByObject(items, {
  id: (i) => i.model.id,
  name: (i) => i.model.name,
  category: (i) => i.category,
});
const groupByMemberId = new Map<string, { group: VersionGroup<Item>; index: number }>();
for (const g of groups) g.members.forEach((m, i) => groupByMemberId.set(m.model.id, { group: g, index: i }));

const membersOf = (tag: string): Item[] =>
  items.filter((i) => i.model.tags.some((t) => t.toLowerCase() === tag.toLowerCase()));

// How many tiles a tag resolves to: groups (objects), not raw models — versions
// of one object collapse into a single tile, so this matches what the grid and
// the filter bar show once the collection is opened.
const tileCountForTag = (tag: string): number =>
  groups.filter((g) =>
    g.members.some((m) => m.model.tags.some((t) => t.toLowerCase() === tag.toLowerCase())),
  ).length;

// The same, for a batch (production run): every model carrying that batchId, and
// how many object-tiles those resolve to. A batch cross-cuts the version groups
// (one object's versions can live in different batches), so the tile count is the
// number of groups with at least one member in the batch.
const membersOfBatch = (batchId: string): Item[] =>
  items.filter((i) => i.model.batchId === batchId);
const tileCountForBatch = (batchId: string): number =>
  groups.filter((g) => g.members.some((m) => m.model.batchId === batchId)).length;

// ── DOM handles ──────────────────────────────────────────────────────────────
const el = (id: string): HTMLElement => document.getElementById(id)!;
/** Escape text interpolated into innerHTML (batch provenance is authored, but
 *  rendering it through this keeps any stray `<`/`&`/`"` literal). */
const esc = (s: string): string =>
  s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const galleryEl = el("gallery");
const detailEl = el("detail");
const stvEl = el("stv");
const gridEl = el("grid");
const emptyEl = el("empty");
const searchEl = el("search") as HTMLInputElement;
const collectionHeadEl = el("collection-head");
const collectionTitleEl = el("collection-title");
const collectionDescEl = el("collection-desc");
const batchMetaEl = el("batch-meta") as HTMLElement;
const batchDescEl = el("batch-desc") as HTMLElement;
const batchDescWrapEl = el("batch-desc-wrap") as HTMLDetailsElement;
const slotTypeMetaEl = el("slot-type-meta") as HTMLElement;
const slotTypeInstrEl = el("slot-type-instr") as HTMLElement;
const slotTypeInstrWrapEl = el("slot-type-instr-wrap") as HTMLDetailsElement;
el("count").textContent = `· ${models.length}`;

// ── Thumbnail studio (one shared GL context for every preview) ───────────────
const studio = new ThumbStudio();
const REST_YAW = 0; // the 3/4 angle a tile rests at when not hovered

// ── Active collection ───────────────────────────────────────────────────────
// A collection click (or a tag click in the detail view) opens that tag's own
// page: the collections row is swapped for a titled, described header and the
// grid shows only that collection's items. The grid still ANDs the free-text
// search on top of it.
let activeTag: string | null = null;
// The batch (production run) page, parallel to a collection page: when set, the
// grid is filtered to that batch and the header shows its provenance. The batch
// *index* (the list of all batches) is its own view, flagged separately.
let activeBatch: string | null = null;
let batchIndex = false;
// The slot type page, parallel to a batch page: when set, the grid is filtered
// to models that fit the slot type. The slot type *index* is its own view.
let activeSlotType: string | null = null;
let slotTypeIndex = false;
// Collections lead the results area, so the free-text search narrows them too.
interface CollectionTile { col: Collection; el: HTMLElement; }
const collectionEntries: CollectionTile[] = [];
// The batch tiles shown on the batch index (`#batches`); hidden in every other
// view. Each links to its batch page (`#batch=<id>`).
interface BatchTile { id: string; label: string; for: string; gen: string; style: string; el: HTMLElement; }
const batchEntries: BatchTile[] = [];
// The slot type tiles, parallel to batch tiles.
interface SlotTypeTile { id: string; label: string; summary: string; el: HTMLElement; }
const slotTypeEntries: SlotTypeTile[] = [];

const membersOfSlotType = (slotTypeId: string): Item[] =>
  items.filter((i) => i.model.fits.includes(slotTypeId));
const tileCountForSlotType = (slotTypeId: string): number =>
  groups.filter((g) => g.members.some((m) => m.model.fits.includes(slotTypeId))).length;

// ── Collections row ────────────────────────────────────────────────────────────
// Pick up to `n` members spread across the list, so a tile's mosaic shows
// variety rather than four near-identical neighbours.
function sample<T>(arr: T[], n: number): T[] {
  if (arr.length <= n) return arr.slice();
  const step = arr.length / n;
  return Array.from({ length: n }, (_, i) => arr[Math.floor(i * step)]!);
}

// A group tile's 2x2 preview mosaic with the member count floated in a light
// disc at its centre, over the four sample renders. The disc is always a perfect
// circle sized to hold up to 3 digits; beyond that the whole circle grows so it
// never stretches into a pill. Shared by the collection, Batches and batch tiles.
function mosaicMarkup(cells: string, count: number): string {
  const digits = String(count).length;
  const grow = digits > 3
    ? ` style="width:${46 + (digits - 3) * 13}px;height:${46 + (digits - 3) * 13}px"`
    : "";
  return `<div class="col-mosaic">${cells}<span class="col-mosaic-count"${grow}>${count}</span></div>`;
}

interface MosaicCanvas { canvas: HTMLCanvasElement; model: Model; }
const pendingMosaics: MosaicCanvas[] = [];

function buildCollections(): void {
  // Collections lead the single grid — their tiles sit ahead of the model tiles
  // in one continuous flow (no separate row or heading). The model cards are
  // already in the grid (built at module load), so insert the collections before
  // the first of them.
  const frag = document.createDocumentFragment();

  // (The batch index lives in the top bar — a "Batches" link next to the brand —
  // not as a leading tile here.)

  // A "Slot types" tile leads the row: the entry to the slot type index (the
  // contracts that govern what content fits a socket). Its mosaic samples one
  // model per slot type.
  if (SLOT_TYPES.length > 0) {
    const tile = document.createElement("a");
    tile.className = "collection";
    tile.href = "#slot-types";
    tile.setAttribute("aria-label", `Slot types (${SLOT_TYPES.length})`);
    const picks = sample(
      SLOT_TYPES.map((s) => membersOfSlotType(s.id)[0]).filter((m): m is Item => !!m),
      4,
    );
    const cells = picks.map(() => `<div class="cell"><canvas width="240" height="240"></canvas></div>`).join("");
    tile.innerHTML =
      mosaicMarkup(cells, SLOT_TYPES.length) +
      `<div class="col-body">` +
        `<div class="col-title">Slot types</div>` +
        `<div class="col-blurb">Socket contracts — what content fits each mount point and how to generate it.</div>` +
      `</div>`;
    const canvases = [...tile.querySelectorAll("canvas")] as HTMLCanvasElement[];
    picks.forEach((m, i) => { const c = canvases[i]; if (c) pendingMosaics.push({ canvas: c, model: m.model }); });
    frag.appendChild(tile);
    collectionEntries.push({ col: { id: "slot-types", label: "Slot types", tag: "#slot-types", blurb: "socket contract mount content generation" }, el: tile });
  }

  for (const col of COLLECTIONS) {
    const members = membersOf(col.tag);
    if (members.length === 0) continue; // never feature an empty package
    const count = tileCountForTag(col.tag); // tiles you'll land on, matching the grid

    const tile = document.createElement("a");
    tile.className = "collection";
    tile.href = `#tag=${encodeURIComponent(col.tag)}`;
    tile.setAttribute("aria-label", `${col.label} (${count})`);

    const picks = sample(members, 4);
    const cells = picks.map(() => `<div class="cell"><canvas width="240" height="240"></canvas></div>`).join("");
    tile.innerHTML =
      mosaicMarkup(cells, count) +
      `<div class="col-body">` +
        `<div class="col-title">${col.label}</div>` +
        `<div class="col-blurb">${col.blurb}</div>` +
      `</div>`;

    const canvases = [...tile.querySelectorAll("canvas")] as HTMLCanvasElement[];
    picks.forEach((m, i) => {
      const canvas = canvases[i];
      if (canvas) pendingMosaics.push({ canvas, model: m.model });
    });

    frag.appendChild(tile);
    collectionEntries.push({ col, el: tile });
  }
  gridEl.insertBefore(frag, gridEl.firstChild);
}

// The batch index: one tile per production run, each previewing a sample of its
// models and showing its generator, style and size. Built once at boot, kept
// hidden until the `#batches` view is entered (toggled by setBatchTilesVisible).
// Each tile links to that batch's own page (`#batch=<id>`).
function buildBatchTiles(): void {
  const frag = document.createDocumentFragment();
  for (const b of BATCHES) {
    const members = membersOfBatch(b.id);
    if (members.length === 0) continue; // never feature an empty run
    const count = tileCountForBatch(b.id);

    const tile = document.createElement("a");
    tile.className = "collection batch-tile";
    tile.href = `#batch=${encodeURIComponent(b.id)}`;
    tile.style.display = "none";
    tile.setAttribute("aria-label", `${b.label} (${count})`);

    const picks = sample(members, 4);
    const cells = picks.map(() => `<div class="cell"><canvas width="240" height="240"></canvas></div>`).join("");
    tile.innerHTML =
      mosaicMarkup(cells, count) +
      `<div class="col-body">` +
        `<div class="col-title">${b.label}</div>` +
        `<div class="col-gen">for <strong>${b.for}</strong> · generated by <strong>${b.generator}</strong></div>` +
        `<div class="col-blurb">${b.style}</div>` +
      `</div>`;

    const canvases = [...tile.querySelectorAll("canvas")] as HTMLCanvasElement[];
    picks.forEach((m, i) => { const c = canvases[i]; if (c) pendingMosaics.push({ canvas: c, model: m.model }); });

    frag.appendChild(tile);
    batchEntries.push({ id: b.id, label: b.label, for: b.for, gen: b.generator, style: b.style, el: tile });
  }
  gridEl.appendChild(frag);
}

// The slot type index: one tile per slot type, each previewing models that fit
// it and showing its summary. Built once at boot, hidden until `#slot-types`.
function buildSlotTypeTiles(): void {
  const frag = document.createDocumentFragment();
  for (const s of SLOT_TYPES) {
    const members = membersOfSlotType(s.id);
    if (members.length === 0) continue;
    const count = tileCountForSlotType(s.id);

    const tile = document.createElement("a");
    tile.className = "collection slot-type-tile";
    tile.href = `#slot-type=${encodeURIComponent(s.id)}`;
    tile.style.display = "none";
    tile.setAttribute("aria-label", `${s.label} (${count})`);

    const picks = sample(members, 4);
    const cells = picks.map(() => `<div class="cell"><canvas width="240" height="240"></canvas></div>`).join("");
    tile.innerHTML =
      mosaicMarkup(cells, count) +
      `<div class="col-body">` +
        `<div class="col-title">${s.label}</div>` +
        `<div class="col-blurb">${s.summary}</div>` +
      `</div>`;

    const canvases = [...tile.querySelectorAll("canvas")] as HTMLCanvasElement[];
    picks.forEach((m, i) => { const c = canvases[i]; if (c) pendingMosaics.push({ canvas: c, model: m.model }); });

    frag.appendChild(tile);
    slotTypeEntries.push({ id: s.id, label: s.label, summary: s.summary, el: tile });
  }
  gridEl.appendChild(frag);
}

// Render the collection/batch mosaics after first paint, yielding between draws
// so the shared studio (which rebuilds per distinct model) never blocks the page
// on load. Hidden batch canvases still render fine; they are ready when shown.
function flushMosaics(): void {
  let idx = 0;
  const drawNext = (): void => {
    const slice = pendingMosaics.slice(idx, idx + 4);
    for (const { canvas, model } of slice) studio.draw(canvas, model, defaultVariant(model), REST_YAW);
    idx += 4;
    if (idx < pendingMosaics.length) requestAnimationFrame(drawNext);
  };
  if (pendingMosaics.length) requestAnimationFrame(drawNext);
}

// ── Model tiles ──────────────────────────────────────────────────────────────
// One tile per object (group). A multi-version tile shows a strip of mini
// previews along the bottom (like the detail view's version switcher); hovering
// a mini swaps the big preview to that version. The big preview spins while the
// tile is hovered; a single-version tile just spins.
interface Tile {
  group: VersionGroup<Item>;
  el: HTMLAnchorElement;
  canvas: HTMLCanvasElement;
  minis: HTMLElement[];
  rendered: boolean;
  raf: number | null;
  yaw: number;
  versionIdx: number;
  // Per-member: does this version match the active filter? Under a collection or
  // search, non-matching versions are greyed out and inert (see setVersionFilter);
  // with no filter every entry is true. `primary` is the version the tile rests
  // on and its link points at — the first matching one.
  allowed: boolean[];
  primary: number;
}
const tiles: Tile[] = [];

// Render a tile's preview once it scrolls into view (scales to large libraries).
const seen = new WeakMap<HTMLElement, Tile>();
const io = new IntersectionObserver((entries) => {
  for (const e of entries) {
    if (!e.isIntersecting) continue;
    const tile = seen.get(e.target as HTMLElement);
    if (tile && !tile.rendered) { drawRest(tile); renderMinis(tile); tile.rendered = true; }
  }
}, { rootMargin: "200px" });

const memberModel = (tile: Tile, i = tile.versionIdx): Model => tile.group.members[i]!.model;

function setActiveMini(tile: Tile): void {
  tile.minis.forEach((d, i) => d.classList.toggle("on", i === tile.versionIdx));
}

// Draw each mini once with its own version at the resting angle.
function renderMinis(tile: Tile): void {
  tile.minis.forEach((mini, i) => {
    const c = mini.querySelector("canvas") as HTMLCanvasElement;
    const m = memberModel(tile, i);
    studio.draw(c, m, defaultVariant(m), REST_YAW);
  });
}

function drawRest(tile: Tile): void {
  tile.yaw = REST_YAW;
  showVersion(tile, tile.primary);
}

// Show version `i` in the big preview: highlight its mini, point the tile's link
// at it, and redraw (the spin loop, if running, picks the change up next frame).
function showVersion(tile: Tile, i: number): void {
  tile.versionIdx = i;
  setActiveMini(tile);
  const m = memberModel(tile);
  tile.el.href = `#${encodeURIComponent(m.id)}`;
  if (tile.raf === null) studio.draw(tile.canvas, m, defaultVariant(m), tile.yaw);
}

// Constrain a tile to the versions matching the active filter. The others are
// greyed out and made inert in the strip; the big preview rests on (and the
// tile's link points at) the first matching version. So opening a tile from the
// Fable collection lands on its Fable version, not the classic base — and the
// non-Fable versions can't be hovered into the preview while the filter is on.
// With no filter active every version matches, so nothing is disabled.
function setVersionFilter(tile: Tile, matched: boolean[]): void {
  tile.allowed = matched;
  const primary = matched.findIndex(Boolean);
  tile.primary = primary >= 0 ? primary : 0;
  tile.minis.forEach((mini, i) => mini.classList.toggle("off", !matched[i]));
  if (tile.rendered) {
    showVersion(tile, tile.primary); // redraw the big preview onto the matching version
  } else {
    // Not drawn yet: point the link at the version it will rest on; the preview
    // and active-mini highlight are set when the tile renders (drawRest).
    tile.versionIdx = tile.primary;
    tile.el.href = `#${encodeURIComponent(memberModel(tile).id)}`;
    setActiveMini(tile);
  }
}

function startHover(tile: Tile): void {
  if (tile.raf !== null) return;
  let last = performance.now();
  const tick = (now: number): void => {
    const dt = Math.min((now - last) / 1000, 0.05); last = now;
    tile.yaw += dt * 0.9;
    const m = memberModel(tile);
    studio.draw(tile.canvas, m, defaultVariant(m), tile.yaw);
    tile.raf = requestAnimationFrame(tick);
  };
  tile.raf = requestAnimationFrame(tick);
}

function stopHover(tile: Tile): void {
  if (tile.raf !== null) { cancelAnimationFrame(tile.raf); tile.raf = null; }
  drawRest(tile); // settle back to the base version at the tidy resting angle
}

for (const group of groups) {
  const base = group.members[0]!.model;
  const multi = group.members.length > 1;
  const category = group.members[0]!.category;

  const card = document.createElement("a");
  card.className = "card";
  card.href = `#${encodeURIComponent(base.id)}`;
  card.setAttribute("aria-label", multi ? `${group.name} (${group.members.length} versions)` : group.name);

  const strip = multi
    ? `<div class="ver-strip">${group.members.map((mem, i) =>
        `<span class="ver-mini${i === 0 ? " on" : ""}" title="${versionLabel(mem.model)}"><canvas width="120" height="120"></canvas></span>`,
      ).join("")}</div>`
    : "";
  const anim = base.animations.length ? `<span class="badge anim">▶ ${base.animations.length}</span>` : "";
  const vars = base.variants.length > 1 ? `<span class="badge">${base.variants.length}×</span>` : "";
  const flagsHtml: string[] = [];
  // Slotable content: a solid LEGO brick (studded piece you drop into a socket).
  if (base.fits.length)
    flagsHtml.push(
      `<span class="thumb-flag" title="Fits into: ${base.fits.join(", ")}">` +
        `<svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor"><rect x="3.6" y="3.3" width="2.4" height="2.9" rx="0.5"/><rect x="8" y="3.3" width="2.4" height="2.9" rx="0.5"/><rect x="2.4" y="5.9" width="9.2" height="5.6" rx="0.9"/></svg>` +
      `</span>`,
    );
  // Socket host: the dashed negative of that same brick — a brick-shaped empty
  // outline, the place where a piece is missing and should be set in.
  if (base.sockets.length)
    flagsHtml.push(
      `<span class="thumb-flag" title="Has ${base.sockets.length} socket${base.sockets.length > 1 ? "s" : ""} — content mounts here">` +
        `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.1" stroke-linejoin="round" stroke-linecap="round" stroke-dasharray="1.5 1.2"><path d="M4 5.9V4h2v1.9M8 5.9V4h2v1.9"/><rect x="2.4" y="5.9" width="9.2" height="5.6" rx="0.9"/></svg>` +
        (base.sockets.length > 1 ? `<span class="flag-n">${base.sockets.length}</span>` : "") +
      `</span>`,
    );
  const flags = flagsHtml.length
    ? `<div class="thumb-flags">${flagsHtml.join("")}</div>`
    : "";
  card.innerHTML =
    `<div class="thumb"><canvas width="480" height="480"></canvas>${strip}${flags}</div>` +
    `<div class="card-body">` +
      `<div class="card-title">${group.name}</div>` +
      `<div class="card-sub"><span class="cat">${category}</span><span class="badges">${vars}${anim}</span></div>` +
    `</div>`;

  const canvas = card.querySelector(".thumb canvas") as HTMLCanvasElement;
  const tile: Tile = {
    group, el: card, canvas,
    minis: [...card.querySelectorAll(".ver-mini")] as HTMLElement[],
    rendered: false, raf: null, yaw: REST_YAW, versionIdx: 0,
    allowed: group.members.map(() => true), primary: 0,
  };
  card.addEventListener("pointerenter", () => { if (tile.rendered) startHover(tile); });
  card.addEventListener("pointerleave", () => stopHover(tile));
  // Hovering a mini swaps the big preview to that version (without leaving the
  // tile) — but only a version the active filter allows; greyed-out ones are inert.
  tile.minis.forEach((mini, i) => mini.addEventListener("pointerenter", () => { if (tile.rendered && tile.allowed[i]) showVersion(tile, i); }));

  gridEl.appendChild(card);
  tiles.push(tile);
  seen.set(card, tile);
  io.observe(card);
}

// ── Search + filter ──────────────────────────────────────────────────────────
// A group tile is shown when ANY of its versions matches, so filtering by a
// version tag (e.g. "fable") still surfaces the merged object.
function applyFilter(): void {
  const query = searchEl.value;
  if (batchIndex) { applyBatchIndexFilter(query); return; }
  if (slotTypeIndex) { applySlotTypeIndexFilter(query); return; }
  const tags = activeTag ? [activeTag] : [];
  const base = search(models, { query, tags }).map((h) => h.model);
  let filtered = base;
  if (activeBatch) filtered = filtered.filter((m) => m.batchId === activeBatch);
  if (activeSlotType) filtered = filtered.filter((m) => m.fits.includes(activeSlotType!));
  const hits = new Set(filtered);
  let shown = 0;
  for (const tile of tiles) {
    const matched = tile.group.members.map((mem) => hits.has(mem.model));
    const show = matched.some(Boolean);
    const wasShown = tile.el.style.display !== "none";
    if (show !== wasShown) tile.el.style.display = show ? "" : "none";
    if (!matchedEqual(tile.allowed, matched)) setVersionFilter(tile, matched);
    if (show) shown++;
  }
  emptyEl.hidden = shown > 0;
  setBatchTilesVisible(false);
  setSlotTypeTilesVisible(false);
  syncChrome(query, shown);
}

function matchedEqual(a: boolean[], b: boolean[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}


function applyBatchIndexFilter(query: string): void {
  for (const tile of tiles) tile.el.style.display = "none";
  setSlotTypeTilesVisible(false);
  const shown = filterBatchTiles(query);
  emptyEl.hidden = shown > 0;
  syncChrome(query, shown);
}

function applySlotTypeIndexFilter(query: string): void {
  for (const tile of tiles) tile.el.style.display = "none";
  setBatchTilesVisible(false);
  const shown = filterSlotTypeTiles(query);
  emptyEl.hidden = shown > 0;
  syncChrome(query, shown);
}

function setBatchTilesVisible(show: boolean): void {
  if (!show) for (const b of batchEntries) b.el.style.display = "none";
}

function setSlotTypeTilesVisible(show: boolean): void {
  if (!show) for (const s of slotTypeEntries) s.el.style.display = "none";
}

function filterBatchTiles(query: string): number {
  const q = query.trim().toLowerCase();
  let shown = 0;
  for (const b of batchEntries) {
    const match = !q || [b.label, b.for, b.gen, b.style, b.id].some((s) => s.toLowerCase().includes(q));
    b.el.style.display = match ? "" : "none";
    if (match) shown++;
  }
  return shown;
}

function filterSlotTypeTiles(query: string): number {
  const q = query.trim().toLowerCase();
  let shown = 0;
  for (const s of slotTypeEntries) {
    const match = !q || [s.label, s.summary, s.id].some((v) => v.toLowerCase().includes(q));
    s.el.style.display = match ? "" : "none";
    if (match) shown++;
  }
  return shown;
}

// On the landing the collections head the same results area, so the free-text
// search runs over them too: a collection matches when the query hits its label,
// tag or blurb. An empty query shows them all.
function collectionMatches(col: Collection, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [col.label, col.tag, col.blurb, col.id].some((s) => s.toLowerCase().includes(q));
}

function filterCollections(query: string): void {
  for (const { col, el } of collectionEntries) {
    el.style.display = collectionMatches(col, query) ? "" : "none";
  }
}

// Drive the page chrome (the collections row vs. a titled header) for whichever
// view is active: the landing (collections lead the grid, search narrows them),
// a collection page, a batch page, or the batch index. The inner views hide the
// collections row and show a header (title, count, description) above the grid.
function syncChrome(query: string, count: number): void {
  const hideCollections = (): void => { for (const { el } of collectionEntries) el.style.display = "none"; };
  batchMetaEl.hidden = true;
  batchDescWrapEl.hidden = true;
  slotTypeMetaEl.hidden = true;
  slotTypeInstrWrapEl.hidden = true;

  if (batchIndex) {
    hideCollections();
    collectionTitleEl.innerHTML = `Batches<span class="collection-count">${count}</span>`;
    collectionDescEl.textContent =
      "Every production run the library grew in — what it was generated for, its " +
      "generating model and the style it was generated under. Open one to browse " +
      "its models.";
    collectionDescEl.hidden = false;
    collectionHeadEl.hidden = false;
    return;
  }
  if (slotTypeIndex) {
    hideCollections();
    collectionTitleEl.innerHTML = `Slot types<span class="collection-count">${count}</span>`;
    collectionDescEl.textContent =
      "Socket contracts that govern what content fits each mount point. " +
      "Each carries generation instructions for an agent creating new content. " +
      "Open one to browse its models and read the contract.";
    collectionDescEl.hidden = false;
    collectionHeadEl.hidden = false;
    return;
  }
  if (activeBatch) {
    hideCollections();
    const b = batchById.get(activeBatch);
    collectionTitleEl.innerHTML =
      `${esc(b ? b.label : activeBatch)}<span class="collection-count">${count}</span>`;
    if (b) {
      const rows: [string, string][] = [
        ["for", b.for],
        ["generated by", b.generator],
        ["models", String(membersOfBatch(b.id).length)],
      ];
      if (b.date) rows.push(["date", b.date]);
      batchMetaEl.innerHTML = rows.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join("");
      batchMetaEl.hidden = false;
      collectionDescEl.textContent = b.style;
      collectionDescEl.hidden = false;
      batchDescEl.textContent = b.description ?? "";
      batchDescWrapEl.hidden = !b.description;
      batchDescWrapEl.open = false;
    } else {
      collectionDescEl.hidden = true;
    }
    collectionHeadEl.hidden = false;
    return;
  }
  if (activeSlotType) {
    hideCollections();
    const s = slotTypeById.get(activeSlotType);
    collectionTitleEl.innerHTML =
      `${esc(s ? s.label : activeSlotType)}<span class="collection-count">${count}</span>`;
    if (s) {
      const rows: [string, string][] = [
        ["models", String(membersOfSlotType(s.id).length)],
      ];
      slotTypeMetaEl.innerHTML = rows.map(([k, v]) => `<dt>${esc(k)}</dt><dd>${esc(v)}</dd>`).join("");
      slotTypeMetaEl.hidden = false;
      collectionDescEl.textContent = s.summary;
      collectionDescEl.hidden = false;
      slotTypeInstrEl.textContent = s.instructions + (s.description ? "\n\n" + s.description : "");
      slotTypeInstrWrapEl.hidden = false;
      slotTypeInstrWrapEl.open = false;
    } else {
      collectionDescEl.hidden = true;
    }
    collectionHeadEl.hidden = false;
    return;
  }
  if (activeTag) {
    hideCollections();
    const col = COLLECTIONS.find((c) => c.tag.toLowerCase() === activeTag!.toLowerCase());
    collectionTitleEl.innerHTML =
      `${col ? col.label : activeTag}<span class="collection-count">${count}</span>`;
    collectionDescEl.textContent = col ? col.blurb : "";
    collectionDescEl.hidden = !col;
    collectionHeadEl.hidden = false;
    return;
  }
  collectionHeadEl.hidden = true;
  filterCollections(query);
}

let filterTimer = 0;
searchEl.addEventListener("input", () => {
  cancelAnimationFrame(filterTimer);
  filterTimer = requestAnimationFrame(applyFilter);
});
// Clicking the brand ("models", top-left) drops every filter and returns to the
// bare landing — no active collection, an empty search — regardless of where you
// were. Going via the hash routes us through route() when it changes; if we are
// already on a hash-less landing (only a search was typed), reset in place.
function resetToLanding(): void {
  searchEl.value = "";
  if (location.hash === "" || location.hash === "#") {
    activeTag = null;
    showGallery();
    applyFilter();
  } else {
    location.hash = "";
  }
}
const brandEl = el("brand");
brandEl.addEventListener("click", resetToLanding);
brandEl.addEventListener("keydown", (e) => {
  if (e.key === "Enter" || e.key === " ") { e.preventDefault(); resetToLanding(); }
});
// Back from a collection page: return to wherever you came from (the landing in
// the common case), falling back to the landing on a deep link with no history.
el("collection-back").addEventListener("click", goBack);
// Esc leaves a collection page (the detail viewer owns Esc while it is open).
window.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && !stvEl.hidden) { goBack(); return; }
  if (e.key === "Escape" && (activeTag || activeBatch || batchIndex || activeSlotType || slotTypeIndex) && !galleryEl.hidden) goBack();
});

// ── State restoration across history ──────────────────────────────────────────
// Going back must land you exactly where you were — the gallery scrolled to the
// same row, with the same collection/search still in force. The browser would
// otherwise reset the scroll the moment we hide the gallery to open a detail, so
// we take scroll restoration into our own hands: every history entry (the
// landing, a collection page, a detail) carries its own scroll offset on its
// `history.state`, and we put it back whenever we return to that entry.
history.scrollRestoration = "manual";

// Every "back" affordance (the detail's "‹ Gallery", a collection's "‹ Back",
// Esc) walks the history back rather than pushing a fresh entry, so the entry we
// return to keeps the scroll/filter state we stored on it. A deep link with no
// history behind it falls back to the landing.
function goBack(): void {
  if (window.history.length > 1) window.history.back();
  else location.hash = "";
}

// Persist the live scroll offset onto the current history entry. Throttled to
// one write per frame and skipped while the detail overlay (not the gallery) is
// what's on screen, so a detail's entry never inherits the gallery's offset.
let scrollSaveScheduled = false;
function rememberScroll(): void {
  scrollSaveScheduled = false;
  if (galleryEl.hidden) return;
  history.replaceState({ ...(history.state as object | null), scroll: window.scrollY }, "");
}
window.addEventListener("scroll", () => {
  if (scrollSaveScheduled || galleryEl.hidden) return;
  scrollSaveScheduled = true;
  requestAnimationFrame(rememberScroll);
}, { passive: true });
// Flush the offset synchronously the instant a tile or collection link navigates
// away, so the entry we leave keeps its exact offset even if the throttled save
// hasn't fired yet this frame.
galleryEl.addEventListener("click", (e) => {
  if ((e.target as HTMLElement).closest("a[href^='#']")) rememberScroll();
}, true);

// Put the gallery back to the offset stored on the entry we just landed on (the
// top — 0 — for a fresh page we've never scrolled, e.g. a just-opened collection
// or a deep link). Called once the grid is laid out (filter applied) so the
// scroll height it targets is the final one.
function restoreScroll(): void {
  window.scrollTo(0, (history.state as { scroll?: number } | null)?.scroll ?? 0);
}

// ── Detail viewer + routing ──────────────────────────────────────────────────
// The variant the user last picked by hand. We carry it forward as the preferred
// default for every object opened afterwards: a new object that defines a variant
// with this id opens on it. The preference is sticky — opening an object that
// lacks the variant simply falls back to its own default, without forgetting the
// choice, so the next object that does have it still honours it. An explicit
// variant in the URL (a deep link) always wins over this default.
let preferredVariant: string | null = null;

const detail = new DetailViewer({
  onClose: () => goBack(),
  onVariantPick: (variantId) => { preferredVariant = variantId; },
  // Resolve another model by id — lets the detail viewer fetch the actor an
  // interaction poses (the default worker).
  resolveModel: (id) => models.find((m) => m.id === id),
  // The models that fit a socket: when the socket has a `slotType`, match models
  // whose `fits` includes it; otherwise fall back to the legacy `accepts` tag
  // filter. Either way, the host itself is excluded.
  mountablesFor: (socket, hostId) =>
    models
      .filter((m) => m.id !== hostId)
      .filter((m) => {
        if (socket.slotType) return m.fits.includes(socket.slotType);
        const accepts = socket.accepts ?? [];
        return !accepts.length || m.tags.some((t) => accepts.includes(t));
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
  // Clicking a tag in the inspector jumps back to the gallery filtered to it.
  onTag: (tag) => { searchEl.value = ""; location.hash = `tag=${encodeURIComponent(tag)}`; },
  // Clicking the batch in the inspector opens that production run's page.
  onBatch: (batchId) => { searchEl.value = ""; location.hash = `batch=${encodeURIComponent(batchId)}`; },
  // A socket heading / a "Fits into" entry opens the slot type's own page — the
  // hub linking content and hosts both ways.
  onSlotType: (slotTypeId) => { searchEl.value = ""; location.hash = `slot-type=${encodeURIComponent(slotTypeId)}`; },
  // The slot type's display label, or undefined when the id names no known type.
  slotTypeLabel: (slotTypeId) => slotTypeById.get(slotTypeId)?.label,
  // The detail viewer draws its version-switcher and variant previews through the shared studio.
  renderThumb: (canvas, model, variantId, tight, slots) => studio.draw(canvas, model, variantId, REST_YAW, tight, slots),
  // Keep the URL in step with the variant on the stage (without re-routing), so a
  // reload or a shared link restores the same variant. replaceState does not fire
  // hashchange, so route() is not re-run.
  onState: (modelId, variantId) => {
    const hash = "#" + encodeURIComponent(modelId) + (variantId ? "~" + encodeURIComponent(variantId) : "");
    if (location.hash !== hash) history.replaceState(history.state, "", hash);
  },
});

// ── Slot type viewer ──────────────────────────────────────────────────────
const stvViewer = new SlotTypeViewer({
  onClose: () => goBack(),
  renderThumb: (canvas, model, variantId, tight) => studio.draw(canvas, model, variantId, REST_YAW, tight),
});

function showGallery(): void {
  detail.close();
  stvViewer.close();
  detailEl.hidden = true;
  stvEl.hidden = true;
  galleryEl.hidden = false;
}

function showDetail(group: VersionGroup<Item>, index: number, variantId?: string): void {
  galleryEl.hidden = true;
  stvEl.hidden = true;
  detailEl.hidden = false;
  // A variant pinned in the URL wins; otherwise carry forward the user's last
  // hand-picked variant as the default (open() ignores it if this object has no
  // such variant, leaving the preference intact for the next one).
  const wanted = variantId ?? preferredVariant ?? undefined;
  detail.open({ name: group.name, members: group.members as DetailItem[] }, index, wanted);
}

function showSlotTypeViewer(slotTypeId: string): void {
  const st = slotTypeById.get(slotTypeId);
  if (!st) return;
  const content = models.filter((m) => m.fits.includes(slotTypeId));
  const hosts = models.filter((m) => m.sockets.some((s) => s.slotType === slotTypeId));
  detail.close(); // park the detail render loop if we arrived here from it
  galleryEl.hidden = true;
  detailEl.hidden = true;
  stvEl.hidden = false;
  stvViewer.open(st, content, hosts);
}

// Hash routes: ""                      → gallery, no filter
//              "tag=<tag>"             → gallery filtered to <tag>
//              "batches"               → the batch index (every production run)
//              "batch=<id>"            → gallery filtered to one batch
//              "<model-id>"            → the detail viewer for that model's object
//              "<model-id>~<variant>"  → the same, opened on a specific variant
function route(): void {
  const raw = location.hash.replace(/^#/, "");
  const clearAll = (): void => { activeTag = null; activeBatch = null; batchIndex = false; activeSlotType = null; slotTypeIndex = false; };
  if (raw === "batches") {
    clearAll(); batchIndex = true;
    showGallery(); applyFilter(); restoreScroll();
    return;
  }
  if (raw === "slot-types") {
    clearAll(); slotTypeIndex = true;
    showGallery(); applyFilter(); restoreScroll();
    return;
  }
  if (raw.startsWith("batch=")) {
    clearAll(); activeBatch = decodeURIComponent(raw.slice(6)) || null;
    showGallery(); applyFilter(); restoreScroll();
    return;
  }
  if (raw.startsWith("slot-type=")) {
    const stId = decodeURIComponent(raw.slice(10));
    if (stId && slotTypeById.has(stId)) { clearAll(); showSlotTypeViewer(stId); return; }
    clearAll(); showGallery(); applyFilter(); restoreScroll();
    return;
  }
  if (raw.startsWith("tag=")) {
    clearAll(); activeTag = decodeURIComponent(raw.slice(4)) || null;
    showGallery(); applyFilter(); restoreScroll();
    return;
  }
  const [idRaw, variantRaw] = raw.split("~");
  const id = decodeURIComponent(idRaw ?? "");
  const variantId = variantRaw ? decodeURIComponent(variantRaw) : undefined;
  const found = id ? groupByMemberId.get(id) : undefined;
  if (found) { showDetail(found.group, found.index, variantId); return; }
  clearAll();
  showGallery();
  applyFilter();
  restoreScroll();
}
window.addEventListener("hashchange", route);

// ── Boot ──────────────────────────────────────────────────────────────────────
buildCollections();
buildBatchTiles();
buildSlotTypeTiles();
flushMosaics();
route();

// The catalogue and tiles are wired up synchronously above (previews then fill
// in lazily). Lift the loading veil on the next frame, once the gallery paints.
const loaderEl = document.getElementById("loader");
if (loaderEl) {
  requestAnimationFrame(() => {
    loaderEl.classList.add("hide");
    setTimeout(() => loaderEl.remove(), 400);
  });
}
